/**
 * TPRM Timezone Service
 * Converts UTC timestamps stored in the database to IST (UTC+5:30) for display.
 *
 * Usage:
 *   import { toIST, formatToIST } from '@/utils/timezoneService'
 *   formatToIST('2024-01-01T10:00:00Z') // "Jan 1, 2024 03:30 PM IST"
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Extend dayjs with plugins
dayjs.extend(utc)
dayjs.extend(timezone)

const IST_ZONE = 'Asia/Kolkata'
const UTC_ZONE = 'UTC'
const DEFAULT_FORMAT = 'MMM D, YYYY hh:mm A'

/**
 * Convert a UTC datetime string/object to a dayjs instance in IST.
 * @param {string|Date|number} value — UTC timestamp (ISO string, Date, or epoch ms)
 * @returns {dayjs.Dayjs|null}
 */
export function toIST(value) {
  if (!value || value === 'null' || value === 'undefined') return null
  try {
    // Parse as UTC then convert to IST
    const dt = dayjs.utc(value)
    if (!dt.isValid()) return null
    return dt.tz(IST_ZONE)
  } catch {
    return null
  }
}

/**
 * Convert a UTC datetime string/object to a dayjs instance in UTC.
 * @param {string|Date|number} value
 * @returns {dayjs.Dayjs|null}
 */
export function toUTC(value) {
  if (!value || value === 'null' || value === 'undefined') return null
  try {
    const dt = dayjs.utc(value)
    if (!dt.isValid()) return null
    return dt
  } catch {
    return null
  }
}

/**
 * Format a UTC timestamp to IST display string.
 * @param {string|Date|number} value — UTC timestamp
 * @param {string} format — dayjs format string (default: 'MMM D, YYYY hh:mm A')
 * @param {string} fallback — string returned when value is invalid
 * @returns {string}
 */
export function formatToIST(value, format = DEFAULT_FORMAT, fallback = '') {
  const dt = toIST(value)
  if (!dt) return fallback
  try {
    return dt.format(format) + ' IST'
  } catch {
    return fallback
  }
}

/**
 * Format a UTC timestamp to a date-only IST display string.
 * @param {string|Date|number} value — UTC timestamp
 * @param {string} fallback
 * @returns {string}
 */
export function formatToISTDate(value, fallback = '') {
  return formatToIST(value, 'MMM D, YYYY', fallback)
}

/**
 * Format a UTC timestamp to a time-only IST display string.
 * @param {string|Date|number} value — UTC timestamp
 * @param {string} fallback
 * @returns {string}
 */
export function formatToISTTime(value, fallback = '') {
  return formatToIST(value, 'hh:mm A', fallback)
}

/**
 * Get relative time from now (e.g., "2 hours ago") in IST context.
 * @param {string|Date|number} value — UTC timestamp
 * @param {string} fallback
 * @returns {string}
 */
export function relativeTimeFromUTC(value, fallback = '') {
  const dt = toIST(value)
  if (!dt) return fallback
  try {
    return dt.fromNow()
  } catch {
    return fallback
  }
}

/**
 * Get current time in IST.
 * @returns {dayjs.Dayjs}
 */
export function nowIST() {
  return dayjs().tz(IST_ZONE)
}

/**
 * Get current time formatted in IST.
 * @param {string} format
 * @returns {string}
 */
export function nowISTFormatted(format = DEFAULT_FORMAT) {
  return nowIST().format(format) + ' IST'
}

/**
 * Bulk convert an array of UTC timestamps to IST strings.
 * @param {Array<string|Date|number>} values
 * @param {string} format
 * @returns {Array<string>}
 */
export function bulkFormatToIST(values, format = DEFAULT_FORMAT) {
  if (!Array.isArray(values)) return []
  return values.map(v => formatToIST(v, format))
}

/**
 * Sugar: same as formatToIST but used as a Vue template filter.
 * @param {string|Date|number} value
 * @param {string} format
 * @returns {string}
 */
export function istDateFilter(value, format = DEFAULT_FORMAT) {
  return formatToIST(value, format, value)
}

// Default export for convenience
export default {
  toIST,
  toUTC,
  formatToIST,
  formatToISTDate,
  formatToISTTime,
  relativeTimeFromUTC,
  nowIST,
  nowISTFormatted,
  bulkFormatToIST,
  istDateFilter
}
